import React, { useState, useEffect } from "react";
import { 
  Search, 
  Shield, 
  Bot, 
  Megaphone, 
  FileCheck, 
  Headphones, 
  Timer, 
  Users, 
  BookOpen, 
  Brain, 
  FileText, 
  Calendar, 
  Key, 
  BarChart3, 
  Calculator, 
  Eye,
  Info,
  Sparkles
} from "lucide-react";

const iconMap = {
  "Access Management": Shield,
  "Agent Assist": Bot,
  "Announcements": Megaphone,
  "CIK Monitoring": FileCheck,
  "Coaching Transcriber": Headphones,
  "Gnosis Timer": Timer,
  "HRPay AI Assistant": Users,
  "IDP Monitoring": BookOpen,
  "MOP Assistant": Brain,
  "Resume Analysis": FileText,
  "MOM Generator": Calendar,
  "Password Manager": Key,
  "QA Audio Transcriber": BarChart3,
  "Rebate Calculator": Calculator,
  "Rebate Viewer": Eye
};

const categoryColors = {
  "AI": "from-purple-500 to-pink-500",
  "Transcription": "from-blue-500 to-cyan-500",
  "Admin": "from-green-500 to-emerald-500",
  "Monitoring": "from-orange-500 to-red-500",
  "Tools": "from-indigo-500 to-purple-500"
};

const allTools = [
  {
    name: "Access Management",
    path: "/access-management",
    description: "Controls user accounts (create, deactivate, reset passwords).",
    category: "Admin",
    whoUses: "Supervisors, Managers",
    howToUse: "Log in → Click Access Management → Use Add/Edit/Delete/Reset.",
    commonErrors: [
      "❗ Can't find user? Use filters by supervisor or department.",
      "❗ Reset not working? Ensure OTP is used within 10 minutes."
    ],
    bestPractices: [
      "Regularly audit inactive accounts.",
      "Always assign least-privilege roles."
    ]
  },
  {
    name: "Agent Assist",
    path: "/agent-assist",
    description: "Provides AI-powered support answers.",
    category: "AI",
    whoUses: "Frontline agents",
    howToUse: "Type a query → Wait for response → Copy/Save for ticket use.",
    commonErrors: [
      "❗ Vague answers? Rephrase your query."
    ],
    bestPractices: [
      "Use for FAQs, not unique edge cases.",
      "Document useful answers in internal wikis."
    ]
  },
  {
    name: "Announcements",
    path: "/admin/announcements",
    description: "Creates internal notices (e.g., updates, memos).",
    category: "Admin",
    whoUses: "Admins, Team Managers",
    howToUse: "Click Announcements → + New Announcement → Fill out & publish.",
    commonErrors: [
      "❗ Not showing to users? Check 'Target Audience' selection."
    ],
    bestPractices: [
      "Use 'Priority 1' for time-sensitive content.",
      "Avoid jargon in company-wide announcements."
    ]
  },

  {
    name: "Coaching Transcriber",
    path: "/coaching-transcriber",
    description: "Analyzes coaching recordings to auto-generate reports.",
    category: "Transcription",
    whoUses: "Coaches, Team Leads",
    howToUse: "Upload MP3/WAV → Click Analyze → Review generated report.",
    commonErrors: [
      "❗ Poor transcript? Ensure audio clarity; avoid background noise."
    ],
    bestPractices: [
      "Review 'Root Cause' & 'Action Plan' with the agent post-session."
    ]
  },
  {
    name: "Gnosis Timer",
    path: "/gnosis",
    description: "Manages multiple active/paused task timers.",
    category: "Tools",
    whoUses: "Support agents handling multiple issues",
    howToUse: "Click Gnosis → Set timer → Pause/Resume as needed.",
    commonErrors: [
      "❗ Timer didn't alert? Ensure tab is active or notifications are enabled."
    ],
    bestPractices: [
      "Use max of 4 timers to avoid multitasking fatigue."
    ]
  },
  {
    name: "HRPay AI Assistant",
    path: "/hrpay",
    description: "Answers HR and payroll-related questions via chatbot.",
    category: "AI",
    whoUses: "All employees",
    howToUse: "Ask question → Rate response → Escalate if unsatisfied.",
    commonErrors: [
      "❗ Didn't answer correctly? Escalate to support ticket."
    ],
    bestPractices: [
      "Use for routine HR questions (leave filing, payslip issues)."
    ]
  },
  {
    name: "IDP Monitoring",
    path: "/idp-monitoring",
    description: "Tracks personal and team learning goals.",
    category: "Monitoring",
    whoUses: "All staff, especially HR and Managers",
    howToUse: "Click IDP Monitoring → View/Edit Progress → Add training if authorized.",
    commonErrors: [
      "❗ Locked out of training updates? You may lack permission."
    ],
    bestPractices: [
      "Update percentage monthly; link to coaching feedback."
    ]
  },
  {
    name: "MOP Assistant",
    path: "/webview",
    description: "Creates, analyzes, or improves Method of Procedures via AI.",
    category: "AI",
    whoUses: "Ops, Engineering, Documentation teams",
    howToUse: "Upload or request new MOP → Receive AI draft → Export.",
    commonErrors: [
      "❗ Bad formatting? Rephrase prompt or manually polish."
    ],
    bestPractices: [
      "Always manually review for compliance."
    ]
  },
  {
    name: "Resume Analysis",
    path: "/resume-analysis",
    description: "Matches resumes against job descriptions.",
    category: "AI",
    whoUses: "TA/Recruitment",
    howToUse: "Upload resume → Enter job description → Click Analyze.",
    commonErrors: [
      "❗ Low fit score? Check resume file format or completeness."
    ],
    bestPractices: [
      "Use 'Generate Interview Questions' to streamline screening."
    ]
  },
  {
    name: "MOM Generator",
    path: "/audio-transcriber",
    description: "Transcribes meetings into MoM summaries.",
    category: "Transcription",
    whoUses: "Admins, Secretaries, Project Leads",
    howToUse: "Upload meeting audio → Review summary → Export.",
    commonErrors: [
      "❗ Transcription error? Use clearer recordings or post-edit."
    ],
    bestPractices: [
      "Always include action items and responsible persons."
    ]
  },
  {
    name: "Password Manager",
    path: "/password-manager",
    description: "Stores and manages system credentials securely.",
    category: "Admin",
    whoUses: "All team members with credentials",
    howToUse: "Add New → Fill Service, Username, Password → Save.",
    commonErrors: [
      "❗ Can't find entry? Use Refresh button."
    ],
    bestPractices: [
      "Don't store personal passwords. Use org accounts only."
    ]
  },
  {
    name: "QA Audio Transcriber",
    path: "/qa-audio-transcriber",
    description: "Transcribes and scores call recordings for quality.",
    category: "Transcription",
    whoUses: "QA Analysts",
    howToUse: "Upload call → Analyze → Review QA scoring categories.",
    commonErrors: [
      "❗ Missing sections? Re-check file type or clarity."
    ],
    bestPractices: [
      "Use reports for coaching, not just compliance."
    ]
  },
  {
    name: "Rebate Calculator",
    path: "/rebate-calculator",
    description: "Calculates billing rebates due to service issues.",
    category: "Tools",
    whoUses: "Agents",
    howToUse: "Enter billing cycle → Input days affected → Submit CARE Summary.",
    commonErrors: [
      "❗ Duplicate ticket number? Check for previous entries."
    ],
    bestPractices: [
      "Always include RFO (root cause) with resolution notes."
    ]
  },
  {
    name: "Rebate Viewer",
    path: "/rebate-viewer",
    description: "Tracks, filters, and approves rebate submissions.",
    category: "Tools",
    whoUses: "Supervisors, QA, Billing Managers",
    howToUse: "Go to Rebate Viewer → Use filters → Approve/Reject entries.",
    commonErrors: [
      "❗ Not seeing team? Set filters by Team Leader or Line of Business."
    ],
    bestPractices: [
      "Use dashboard pie chart to assess processing delays."
    ]
  }
];

const categories = ["All", "AI", "Transcription", "Admin", "Monitoring", "Tools"];

const ToolsOverview = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("All");
  const [hoveredTool, setHoveredTool] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const filteredTools = allTools.filter((tool) =>
    tool.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
    (activeTab === "All" || tool.category === activeTab)
  );

  const handleCardClick = (path) => {
    window.location.href = path;
  };

  const handleInfoHover = (tool, event) => {
    const rect = event.target.getBoundingClientRect();
    setTooltipPosition({
      x: Math.min(rect.right - 320, window.innerWidth - 340),
      y: rect.bottom + 10
    });
    setHoveredTool(tool);
  };

  const handleInfoLeave = () => {
    setHoveredTool(null);
  };

  return (
<div className="min-h-screen">

      <div className={`relative z-10 p-6 sm:p-10 transition-all duration-1000 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="relative">
              <Sparkles className="w-10 h-10 text-yellow-500 dark:text-yellow-400 animate-spin" style={{animationDuration: '3s'}} />
              <div className="absolute inset-0 w-10 h-10 bg-yellow-500/20 rounded-full blur animate-pulse"></div>
            </div>
            <h1 className="text-5xl font-black bg-gradient-to-r from-yellow-400 via-yellow-500 to-gray-400 bg-clip-text text-transparent">
              Operations Hub
            </h1>
          </div>
          <p className="text-lg text-gray-600 dark:text-gray-300 font-medium">
            Your AI-Powered Workspace Tools
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-md mx-auto mb-10">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl blur opacity-25 group-hover:opacity-40 transition-opacity duration-300"></div>
            <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-white/20 dark:border-gray-700/30">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search tools..."
                className="w-full pl-12 pr-4 py-4 bg-transparent text-gray-800 dark:text-white placeholder-gray-500 focus:outline-none text-sm font-medium"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((cat, index) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`relative px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 transform hover:scale-105 ${
                activeTab === cat
                  ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25"
                  : "bg-white/60 dark:bg-gray-800/60 backdrop-blur-xl text-gray-700 dark:text-gray-300 hover:bg-white/80 dark:hover:bg-gray-800/80 border border-white/20 dark:border-gray-700/30"
              }`}
              style={{
                animationDelay: `${index * 100}ms`,
                animation: isLoaded ? 'slideInUp 0.6s ease-out forwards' : 'none'
              }}
            >
              {activeTab === cat && (
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-700 rounded-xl blur opacity-30 -z-10"></div>
              )}
              {cat}
            </button>
          ))}
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6 max-w-7xl mx-auto">
          {filteredTools.map((tool, idx) => {
            const IconComponent = iconMap[tool.name] || Bot;
            const gradientClass = categoryColors[tool.category] || "from-gray-500 to-gray-600";
            
            return (
              <div
                key={idx}
                onClick={() => handleCardClick(tool.path)}
                className="group cursor-pointer"
                style={{
                  animationDelay: `${idx * 50}ms`,
                  animation: isLoaded ? 'fadeInUp 0.6s ease-out forwards' : 'none',
                  opacity: isLoaded ? 1 : 0
                }}
              >
                <div className="relative h-full">
                  {/* Animated border */}
                  <div className="absolute inset-0 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 p-0.5">
                    <div className="w-full h-full bg-white dark:bg-gray-900 rounded-2xl"></div>
                  </div>
                  
                  {/* Card content */}
                  <div className="relative bg-white/70 dark:bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border border-white/20 dark:border-gray-700/30 h-full transition-all duration-300 transform group-hover:scale-[1.02] group-hover:shadow-xl group-hover:shadow-indigo-500/10">
                    
                    {/* Info Button */}
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div 
                        className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 rounded-full flex items-center justify-center cursor-help transition-all duration-200 shadow-lg" 
                        onClick={(e) => e.stopPropagation()}
                        onMouseEnter={(e) => handleInfoHover(tool, e)}
                        onMouseLeave={handleInfoLeave}
                      >
                        <Info className="w-4 h-4 text-white" />
                      </div>
                    </div>

                    {/* Icon */}
                    <div className="mb-6">
                      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${gradientClass} p-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                        <IconComponent className="w-full h-full text-white" />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="space-y-3">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-300">
                        {tool.name}
                      </h3>
                      
                      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                        {tool.description}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r ${gradientClass} text-white shadow-sm`}>
                          {tool.category}
                        </span>
                        
                        <div className="w-6 h-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-2 h-2 bg-white rounded-full transform group-hover:scale-110 transition-transform duration-200"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredTools.length === 0 && (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-full mb-6">
              <Search className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No tools found</h3>
            <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or category filter</p>
          </div>
        )}

        {/* Enhanced Tooltip */}
        {hoveredTool && (
          <div 
            className="fixed w-80 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 dark:border-gray-700/30 p-6 pointer-events-none transition-all duration-200 z-50"
            style={{ 
              left: `${tooltipPosition.x}px`, 
              top: `${tooltipPosition.y}px`,
            }}
          >
            <div className="flex items-center gap-3 mb-4">
              {React.createElement(iconMap[hoveredTool.name] || Bot, {
                className: `w-6 h-6 text-indigo-600 dark:text-indigo-400`
              })}
              <h3 className="font-bold text-gray-900 dark:text-white text-lg">
                {hoveredTool.name}
              </h3>
            </div>
            
            <div className="space-y-4 text-sm max-h-72 overflow-y-auto">
              <div>
                <h4 className="font-semibold text-indigo-600 dark:text-indigo-400 mb-2 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Who Uses This
                </h4>
                <p className="text-gray-700 dark:text-gray-300">{hoveredTool.whoUses}</p>
              </div>

              <div>
                <h4 className="font-semibold text-indigo-600 dark:text-indigo-400 mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4" />
                  How to Use
                </h4>
                <p className="text-gray-700 dark:text-gray-300">{hoveredTool.howToUse}</p>
              </div>

              <div>
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">Common Issues</h4>
                <ul className="space-y-1">
                  {hoveredTool.commonErrors.map((error, errorIdx) => (
                    <li key={errorIdx} className="text-gray-700 dark:text-gray-300 text-xs">
                      {error}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">Best Practices</h4>
                <ul className="space-y-1">
                  {hoveredTool.bestPractices.map((practice, practiceIdx) => (
                    <li key={practiceIdx} className="text-gray-700 dark:text-gray-300 text-xs flex items-start gap-2">
                      <div className="w-1.5 h-1.5 bg-green-500 rounded-full mt-1.5 flex-shrink-0"></div>
                      {practice}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default ToolsOverview;